import { FC, Suspense, useState, useCallback } from 'react';
import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import { Platforms } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import clsx from 'clsx';
import { MAX_LINES_IN_ABOUT } from '@utils/constants';
import ShowMoreText from '../../ShowMoreText';
import Certifications from '../Certifications';
import { getRenderedCertificationCount } from '../Certifications/CertificationsUtils';
import ContactDetails from './ContactDetails';
import s from './About.module.scss';

type Props = {
  about?: string | null;
  platforms?: Platforms;
  certifications: PublicCertification[];
  onShowLess?: () => void;
};

const AboutDesktop: FC<Props> = ({
  about,
  platforms,
  certifications,
  onShowLess,
}) => {
  const [isShowMore, setIsShowMore] = useState(false);
  const [gradientState, setGradientState] = useState({
    showLeft: false,
    showRight: false,
  });
  const { t } = useTranslation();
  const certificationCount = getRenderedCertificationCount(certifications);

  const handleGradientStateChange = useCallback(
    (showLeft: boolean, showRight: boolean) => {
      setGradientState({ showLeft, showRight });
    },
    [],
  );

  const handleShowLessClick = useCallback(() => {
    setIsShowMore(false);
    onShowLess?.();
  }, [onShowLess]);

  return (
    <Box direction="horizontal" className={s.about}>
      <Box
        dataHook="about-container"
        className={clsx(
          s.aboutContainer,
          s[`certCount${certificationCount}`],
          isShowMore && s.showMore,
        )}
        direction="vertical"
      >
        <Text as="h2" size="medium" weight="bold" dataHook="about-header">
          {t('profile-page-about-title')}
        </Text>
        <Box minHeight="24px" />
        <ShowMoreText
          originalText={about}
          textSize="medium"
          handleShowMoreClick={() => setIsShowMore(true)}
          handleShowLessClick={handleShowLessClick}
          maxLines={MAX_LINES_IN_ABOUT}
          showMoreButtonText={t(
            'profile-page-about-section-show-more-text-button',
          )}
          showLessButtonText={t(
            'profile-page-about-section-show-less-text-button',
          )}
        />
      </Box>

      <Box
        dataHook="location-social-certifications-container"
        direction="vertical"
        className={clsx(s.detailsContainer, isShowMore && s.showMore)}
      >
        <ContactDetails
          platforms={platforms}
          isMobileView={false}
          className={clsx(
            s.locationAndSocialContainer,
            s[`certCount${certificationCount}`],
            !isShowMore && s.showLessMode,
          )}
        />

        <Box
          className={clsx(
            s.certificationsContainer,
            s[`certCount${certificationCount}`],
            !isShowMore && s.showLessMode,
            gradientState.showLeft && s.leftGradient,
            gradientState.showRight && s.rightGradient,
          )}
          visibility={certificationCount > 0 ? 'visible' : 'hidden'}
          direction="vertical"
        >
          <Suspense fallback="">
            <Certifications
              isMobileView={false}
              variant="detailed"
              data={certifications}
              onGradientStateChange={handleGradientStateChange}
            />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutDesktop;
